// React
import React, { FormEventHandler, useState } from 'react';

// Vendors
import { Button, Checkbox, SectionalBanner, TextInput } from '@equisoft/design-elements-react';
// import ReactGoogleReCAPTCHA from 'react-google-recaptcha';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import jwtDecode from 'jwt-decode';

// Src
import { ReactComponent as CentralizeLogo } from '@/assets/centralize-logo.svg';
import useLoginMutation from '@/domain/authentication/hooks/useLoginMutation';
import { updateAuthAction } from '@/domain/authentication/reducers/authenticationReducer';
import { JWTAuthPayload } from '@/domain/authentication/types/authenticationTypes';
import { useAppDispatch } from '@/infrastructure/redux/hooks/useAppDispatch';

// Styles
import classes from './Login.module.scss';

const LoginPage = () => {
    // Hooks
    const loginMutation = useLoginMutation();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    // States
    const [usernameValue, setUsernameValue] = useState('');
    const [passwordValue, setPasswordValue] = useState('');
    const [rememberMe, setRememberMe] = useState(false);

    // Handlers
    const handleSubmit: FormEventHandler = async (e) => {
        e.preventDefault();

        loginMutation.mutate(
            {
                username: usernameValue,
                password: passwordValue,
                ipAddress: '127.0.0.1',
                stationName: 'workstation',
                softwareId: 'PA ',
                softwareVersion: '1,0',
            },
            {
                onSuccess: (result) => {
                    const loggedUserInfo = result.data;
                    const authPayload = jwtDecode<JWTAuthPayload>(loggedUserInfo.access_token);
                    dispatch(
                        updateAuthAction({
                            loggedIn: true,
                            loggedUserInfo,
                            userId: authPayload.userId,
                        })
                    );
                },
            }
        );
    };

    // Renders
    const renderErrorMessage = () => {
        if (loginMutation.isError && loginMutation.error instanceof AxiosError && loginMutation.error.response) {
            const errorMessage = loginMutation.error.response.status === 401 ? t('app.page.login.error_message_incorrect_username_or_password') : loginMutation.error.response.data.message;
            return (
                <SectionalBanner
                    className={classes.Alert}
                    type="alert"
                >
                    {errorMessage || 'Unknown error'}
                </SectionalBanner>
            );
        }
        return null;
    };

    return (
        <div className={classes.Root}>
            <div className={classes.MainContainer}>
                <div className={classes.LogoWrapper}>
                    <div className={classes.LogoCenter}>
                        <CentralizeLogo className={classes.Logo} />
                        <div className={classes.Separator} />
                        <div className={classes.TextLogo}>{t('common.logo_title')}</div>
                    </div>
                </div>
                <div className={classes.LoginForm}>
                    <h1 className={classes.FormTitle}>{t('app.page.login.title')}</h1>

                    <form onSubmit={handleSubmit}>
                        <TextInput
                            hint={t('app.page.login.username_hint')}
                            label={t('app.page.login.username_label')}
                            value={usernameValue}
                            onChange={(e) => setUsernameValue(e.target.value)}
                        />

                        <TextInput
                            hint={t('app.page.login.password_hint')}
                            label={t('app.page.login.password_label')}
                            type="password"
                            value={passwordValue}
                            onChange={(e) => setPasswordValue(e.target.value)}
                        />

                        <Link
                            className={classes.ForgotPasswordLink}
                            to="/forgot-password"
                        >
                            {t('app.page.login.forgot_password_label')}
                        </Link>

                        <div className={classes.RememberMeCheckbox}>
                            <Checkbox
                                label={t('app.page.login.remember_me_label')}
                                name="remember_me"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                        </div>

                        {/* <div className={classes.ReCaptchaWrapper}>
                            <ReactGoogleReCAPTCHA
                                sitekey="6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"
                                onChange={() => null}
                                size="normal"
                            />
                        </div> */}

                        <div>
                            <Button
                                buttonType="primary"
                                label={t('app.page.login.submit_button')}
                                type="submit"
                            />
                        </div>

                        {renderErrorMessage()}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
